import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag, User, LogOut, ShoppingCart, ChevronDown, Moon, Sun } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { soundEffects } from '../audio/SoundEffects';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import PrimeDrawer from '../cart/PrimeDrawer';
import { track } from '@vercel/analytics';
import MobileZoomMenu from './MobileZoomMenu';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    // const [isCartOpen, setIsCartOpen] = useState(false); // Global cart state used now
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null); // Pour le dropdown desktop

    const location = useLocation();
    const navigate = useNavigate();
    const { cartCount, isCartOpen, setIsCartOpen } = useCart();
    const { user, logout } = useAuth();
    const { isDark, toggleDarkMode } = useTheme();



    const navTo = (path) => {
        soundEffects.play('click');
        track('Navigation', { path: path, from: location.pathname });
        navigate(path);
        setIsOpen(false);
        setActiveDropdown(null);
    };

    const openCart = () => {
        soundEffects.play('click');
        track('Open Cart', { location: location.pathname });
        setIsCartOpen(true);
    };
    const closeCart = () => setIsCartOpen(false);

    const handleLogout = () => {
        track('Logout');
        logout();
        soundEffects.play('click');
        setIsUserMenuOpen(false);
        navigate('/');
    };

    const isActive = (path) => location.pathname === path;

    const links = [
        { path: '/', label: 'ACCUEIL' },
        { path: '/blog', label: 'BLOG' },
        { path: '/track', label: 'SUIVRE MA COMMANDE' },
        { path: '/custom', label: 'ESPACE PRO' },
        { path: '/contact', label: 'CONTACT' },
    ];

    const [isVisible, setIsVisible] = useState(false); // Hidden initially at top
    const [lastScrollY, setLastScrollY] = useState(0);
    const [isHoveringTop, setIsHoveringTop] = useState(false); // New: Show on mouse hover at top
    const [isSnapActive, setIsSnapActive] = useState(false); // New: Hide on magnetic snap sections

    // Mouse detection: Show navbar when mouse is at the top
    React.useEffect(() => {
        const handleMouseMove = (e) => {
            if (e.clientY < 50) {
                setIsHoveringTop(true);
            } else {
                setIsHoveringTop(false);
            }
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    useEffect(() => {
        const controlNavbar = () => {
            if (typeof window !== 'undefined') {
                const currentScrollY = window.scrollY;

                if (currentScrollY < 10) {
                    // At top: Hide to show full hero
                    setIsVisible(false);
                } else if (currentScrollY < lastScrollY) {
                    // Scrolling UP: Show
                    setIsVisible(true);
                } else {
                    // Scrolling DOWN: Hide
                    setIsVisible(false);
                }
                setLastScrollY(currentScrollY);
            }
        };

        window.addEventListener('scroll', controlNavbar);
        return () => window.removeEventListener('scroll', controlNavbar);
    }, [lastScrollY]);

    // New: Snap Section Detection
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const isAnySnapVisible = entries.some(entry => entry.isIntersecting);
                setIsSnapActive(isAnySnapVisible);
            },
            { threshold: 0.5 } // Trigger when 50% of the snap section is visible
        );

        const snapTargets = document.querySelectorAll('[data-snap-target]');
        snapTargets.forEach(target => observer.observe(target));

        return () => observer.disconnect();
    }, [location.pathname]);

    // 🧲 Listen for custom buybox-snap event from HomePage
    useEffect(() => {
        const handleBuyboxSnap = (e) => {
            setIsSnapActive(e.detail.locked);
        };

        window.addEventListener('buybox-snap', handleBuyboxSnap);
        return () => window.removeEventListener('buybox-snap', handleBuyboxSnap);
    }, []);

    // HIDE NAVBAR ON IMMERSIVE PRODUCT PAGE (God Mode)
    if (location.pathname.startsWith('/produit')) return null;

    return (
        <>
            <nav
                className={`fixed w-full z-50 top-0 px-6 md:px-12 py-4 flex justify-between items-center glass border-b-0 transition-transform duration-300 ${(isOpen || isHoveringTop || (isVisible && !isSnapActive)) ? 'translate-y-0' : '-translate-y-full'}`}
            >
                {/* Logo */}
                <div className="cursor-pointer clickable relative z-50" onClick={() => navTo('/')}>
                    <img src="/assets/logo.webp" alt="Unbox Creator" className="h-10 md:h-14 w-auto object-contain" />
                </div>

                {/* Desktop Menu */}
                <div className="hidden md:flex gap-8 text-xs font-bold uppercase tracking-[0.1em] text-[var(--text)] items-center">
                    {links.map((link) => (
                        <div
                            key={link.label}
                            className="relative group"
                            onMouseEnter={() => link.children && setActiveDropdown(link.label)}
                            onMouseLeave={() => link.children && setActiveDropdown(null)}
                        >
                            <button
                                onClick={() => !link.children && navTo(link.path)}
                                className={`flex items-center gap-1 hover:text-[var(--primary)] transition clickable py-4 ${isActive(link.path) ? 'text-[var(--primary)]' : ''}`}
                            >
                                {link.label}
                                {link.children && <ChevronDown size={14} className={`transition-transform duration-200 ${activeDropdown === link.label ? 'rotate-180' : ''}`} />}
                            </button>

                            {/* Dropdown Menu */}
                            {link.children && (
                                <AnimatePresence>
                                    {activeDropdown === link.label && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 10 }}
                                            transition={{ duration: 0.2 }}
                                            className="absolute top-full left-0 w-64 bg-white border border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] rounded-lg overflow-hidden py-2"
                                        >
                                            {link.children.map((child) => (
                                                <button
                                                    key={child.path}
                                                    onClick={() => navTo(child.path)}
                                                    className="block w-full text-left px-6 py-3 text-black hover:bg-gray-100 hover:text-[var(--primary)] font-bold text-sm transition-colors"
                                                >
                                                    {child.label}
                                                </button>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            )}

                            {!link.children && (
                                <span
                                    className={`absolute bottom-2 left-0 h-[2px] bg-[var(--primary)] transition-all duration-300 ${isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'}`}
                                />
                            )}
                        </div>
                    ))}
                </div>

                {/* Desktop Actions */}
                <div className="hidden md:flex gap-4 items-center">
                    {/* Cart Button */}
                    <button
                        onClick={openCart}
                        className="relative w-12 h-12 bg-[#00A8E1] rounded-full flex items-center justify-center text-white hover:bg-[#008dbd] transition-all duration-300 shadow-md clickable"
                    >
                        <ShoppingCart size={22} fill="currentColor" />

                        <AnimatePresence>
                            {cartCount > 0 && (
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0 }}
                                    className="absolute -top-1 -right-1 w-5 h-5 bg-[#00A8E1] text-white text-[10px] font-black rounded-full flex items-center justify-center shadow-lg border-2 border-white"
                                >
                                    {cartCount}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </button>

                    {/* User Account */}
                    {user ? (
                        <div className="relative">
                            <button
                                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                className="w-12 h-12 border border-[var(--border)] rounded-full flex items-center justify-center hover:bg-[var(--primary)] hover:text-[var(--bg)] transition-all duration-300 text-[var(--text)] clickable"
                            >
                                <User size={20} />
                            </button>
                            <AnimatePresence>
                                {isUserMenuOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="absolute right-0 mt-2 w-48 bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-lg overflow-hidden"
                                    >
                                        <button
                                            onClick={handleLogout}
                                            className="w-full flex items-center gap-2 px-4 py-3 text-left text-[var(--text)] hover:bg-[var(--primary)] hover:text-[var(--bg)] transition-colors"
                                        >
                                            <LogOut size={16} />
                                            Déconnexion
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ) : (
                        <button
                            onClick={() => {
                                track('Login Click');
                                navTo('/login');
                            }}
                            className="border border-[var(--border)] px-6 py-3 rounded-full text-xs uppercase tracking-widest hover:bg-[var(--primary)] hover:text-[var(--bg)] transition-all duration-300 font-bold text-[var(--text)] clickable"
                        >
                            Se connecter
                        </button>
                    )}
                </div>

                {/* Mobile Actions & Menu */}
                <div className="md:hidden z-50 flex items-center gap-4">
                    {/* CART BUTTON (Replicated from ProductPage) */}
                    <button
                        onClick={() => setIsCartOpen(true)}
                        className="group relative w-10 h-10 rounded-full border border-[var(--text)]/10 flex items-center justify-center transition-all duration-500 hover:bg-[var(--primary)] text-[var(--text)] hover:text-[var(--bg)]"
                    >
                        <ShoppingBag size={18} className="group-hover:scale-110 transition-transform" />

                        {/* DIGITAL INDICATOR (Badge) */}
                        <AnimatePresence>
                            {cartCount > 0 && (
                                <motion.div
                                    initial={{ scale: 0, x: 5, y: -5 }}
                                    animate={{ scale: 1, x: 0, y: 0 }}
                                    exit={{ scale: 0 }}
                                    className="absolute -top-1 -right-1 w-4 h-4 bg-[var(--primary)] text-white text-[9px] font-black rounded-full flex items-center justify-center shadow-lg border-2 border-white"
                                >
                                    {cartCount}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Dashed Orbit */}
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{
                                rotate: { duration: 10, repeat: Infinity, ease: "linear" }
                            }}
                            className="absolute inset-0 border border-[var(--primary)]/30 border-dashed rounded-full"
                        />
                    </button>

                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="p-2 text-[var(--text)] hover:text-[var(--primary)] transition-colors"
                    >
                        {isOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </nav>

            {/* Custom Mobile Zoom Menu (Camera Dial Style) */}
            <MobileZoomMenu isOpen={isOpen} onClose={() => setIsOpen(false)} />
            <PrimeDrawer isOpen={isCartOpen} onClose={closeCart} />
        </>
    );
};

export default Navbar;
